import { useEffect } from "react";
import { config } from "./config";

const QUIET_TIME = 5000;
const CHECK_INTERVAL = 60000;
const editable = 'input:not([type="range"]):not([type="checkbox"]):not([type="radio"]):not([type="button"]):not([type="submit"]),textarea,select,[contenteditable="true"]';

function canReload(document: Document, embedded = false): boolean {
  if (document.fullscreenElement || document.querySelector('dialog[open],[aria-busy="true"],[data-unsaved="true"],form button:disabled')) return false;
  if (document.activeElement?.matches(editable)) return false;
  // Inputs inside frames do not bubble events to the parent, so keep their page intact.
  if (embedded && document.querySelector(editable)) return false;
  if (Array.from(document.querySelectorAll("video,audio")).some(element => {
    const media = element as HTMLMediaElement;
    return !media.paused && !media.ended;
  })) return false;
  for (const frame of document.querySelectorAll("iframe")) {
    try {
      // A cross-origin player's activity cannot be inspected: update on navigation instead.
      if (!frame.contentDocument || !canReload(frame.contentDocument, true)) return false;
    } catch { return false; }
  }
  return true;
}

export default function SilentUpdates() {
  useEffect(() => {
    if (!import.meta.env.PROD || !("serviceWorker" in navigator)) return;
    let disposed = false;
    let registration: ServiceWorkerRegistration | undefined;
    let checking = false;
    let pending = false;
    let dirty = false;
    let interacted = false;
    let reloading = false;
    let lastInteraction = Date.now();
    let timer: ReturnType<typeof setTimeout> | undefined;
    // First installation must never reload a visitor's page.
    let controlled = Boolean(navigator.serviceWorker.controller);
    const reloadKey = `yestv:${config.environment}:auto-update`;

    function apply() {
      if (disposed || !pending || reloading || dirty || !navigator.onLine || document.visibilityState !== "visible") return;
      if (Date.now() - lastInteraction < QUIET_TIME || !canReload(document)) return;
      try {
        // A failed or interrupted activation must not create a reload loop.
        if (sessionStorage.getItem(reloadKey) === config.build) return;
        sessionStorage.setItem(reloadKey, config.build);
      } catch { /* The in-memory guard still prevents repeated reloads in this document. */ }
      reloading = true;
      location.reload();
    }
    function schedule() {
      clearTimeout(timer);
      timer = setTimeout(apply, QUIET_TIME);
    }
    function interaction() {
      interacted = true;
      lastInteraction = Date.now();
      clearTimeout(timer);
    }
    function editing(event: Event) {
      if (event.target instanceof Element && event.target.matches(editable + ',input[type="checkbox"],input[type="radio"]')) dirty = true;
      interaction();
    }
    function changed() {
      if (controlled) {
        pending = true;
        // An untouched launch can move straight to the new version; active visits wait for a return.
        if (!interacted) schedule();
      }
      controlled = true;
    }
    async function check() {
      if (disposed || checking || !registration || !navigator.onLine || document.visibilityState !== "visible") return;
      checking = true;
      try {
        await registration.update();
        registration.waiting?.postMessage({ type: "YESTV_ACTIVATE_IF_COMPATIBLE" });
      } catch { /* Offline/interrupted updates leave the current version usable. */ }
      finally { checking = false; }
    }
    function resumed() {
      if (document.visibilityState !== "visible") return;
      void check();
      if (pending) schedule();
    }
    function capability(event: MessageEvent) {
      if (event.data?.type === "YESTV_UPDATE_CAPABILITY") event.ports[0]?.postMessage({ protocol: "yestv-silent-v1" });
    }

    // Use the browser API directly: the plugin's autoUpdate client reloads every tab immediately.
    void navigator.serviceWorker.register("/sw.js", { scope: "/", updateViaCache: "none" }).then(value => {
      if (disposed) return;
      registration = value;
      void check();
    }).catch(() => { /* Browsing remains available when SW registration is unavailable. */ });
    navigator.serviceWorker.addEventListener("controllerchange", changed);
    navigator.serviceWorker.addEventListener("message", capability);
    document.addEventListener("visibilitychange", resumed);
    window.addEventListener("pageshow", resumed);
    window.addEventListener("focus", resumed);
    window.addEventListener("online", resumed);
    document.addEventListener("pointerdown", interaction, true);
    document.addEventListener("keydown", interaction, true);
    document.addEventListener("scroll", interaction, true);
    document.addEventListener("input", editing, true);
    document.addEventListener("change", editing, true);
    const interval = setInterval(check, CHECK_INTERVAL);
    return () => {
      disposed = true;
      clearTimeout(timer);
      clearInterval(interval);
      navigator.serviceWorker.removeEventListener("controllerchange", changed);
      navigator.serviceWorker.removeEventListener("message", capability);
      document.removeEventListener("visibilitychange", resumed);
      window.removeEventListener("pageshow", resumed);
      window.removeEventListener("focus", resumed);
      window.removeEventListener("online", resumed);
      document.removeEventListener("pointerdown", interaction, true);
      document.removeEventListener("keydown", interaction, true);
      document.removeEventListener("scroll", interaction, true);
      document.removeEventListener("input", editing, true);
      document.removeEventListener("change", editing, true);
    };
  }, []);
  return null;
}
